import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Input, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import { DATE_FORMATS } from '@/utils/formUtils';
import { TRAVEL_DOMESTIC_CURRENCY } from './constants';

const API_DATE = 'YYYY-MM-DD';

const formatAmount = (value, currency) =>
  value === null || value === undefined
    ? '-'
    : `${new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value) || 0)}${currency ? ` ${currency}` : ''}`;

const formatRate = (value) =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(Number(value) || 0);

/**
 * Exchange rates of the travel order and the settlement per currency.
 *
 * Each foreign currency on the order gets a rate: pick the day and the server
 * fetches the NBS middle rate of that day, or type the rate in and it is kept
 * as typed. Below, every currency's payout with its dinar equivalent — the
 * numbers the journal entry is booked with.
 */
export default function TravelCurrencySettlement({
  travelExchangeRates = [],
  travelOrderCalculation = {},
  disabled,
  isPending,
  onSubmit,
}) {
  const { t } = useTranslation(['officialTravel']);
  const [rates, setRates] = useState([]);

  useEffect(() => {
    setRates(
      (travelExchangeRates || []).map((entry) => ({
        currency: entry.currency,
        rateDate: entry.rateDate || null,
        rate: entry.rate ?? null,
        isManual: Boolean(entry.isManual),
      }))
    );
  }, [travelExchangeRates]);

  const updateRate = (currency, patch) =>
    setRates((current) =>
      current.map((entry) =>
        entry.currency === currency ? { ...entry, ...patch } : entry
      )
    );

  const currencies = useMemo(
    () => travelOrderCalculation?.currencies || [],
    [travelOrderCalculation]
  );

  if (!rates.length && !currencies.length) return null;

  const rateColumns = [
    {
      title: t('currencies.currency', { ns: 'officialTravel' }),
      dataIndex: 'currency',
      key: 'currency',
      width: 90,
    },
    {
      title: t('currencies.rateDate', { ns: 'officialTravel' }),
      key: 'rateDate',
      width: 200,
      render: (_, entry) => (
        <DatePicker
          className="w-full"
          format={DATE_FORMATS.DISPLAY}
          value={entry.rateDate ? dayjs(entry.rateDate, API_DATE) : null}
          disabled={disabled}
          // A new day means a new NBS rate, so a typed rate gives way.
          onChange={(value) =>
            updateRate(entry.currency, {
              rateDate: value ? value.format(API_DATE) : null,
              rate: null,
              isManual: false,
            })
          }
        />
      ),
    },
    {
      title: t('currencies.rate', { ns: 'officialTravel' }),
      key: 'rate',
      // Room for a four-decimal rate next to its NBS / manual tag.
      width: 220,
      render: (_, entry) => (
        <Input
          type="number"
          min={0}
          step="0.0001"
          value={entry.rate ?? ''}
          disabled={disabled}
          suffix={
            entry.isManual ? (
              <Tag className="m-0">
                {t('currencies.manual', { ns: 'officialTravel' })}
              </Tag>
            ) : (
              <Tag color="blue" className="m-0">
                NBS
              </Tag>
            )
          }
          onChange={(event) => {
            const { value } = event.target;
            updateRate(entry.currency, {
              rate: value === '' ? null : Number(value),
              isManual: value !== '',
            });
          }}
        />
      ),
    },
  ];

  const settlementColumns = [
    {
      title: t('currencies.currency', { ns: 'officialTravel' }),
      dataIndex: 'currency',
      key: 'currency',
    },
    ...[
      'totalCalculation',
      'paymentInAdvance',
      'paidWithCompanyCard',
      'totalAmountForPayment',
    ].map((key) => ({
      title: t(`currencies.${key}`, { ns: 'officialTravel' }),
      key,
      align: 'right',
      render: (_, line) => formatAmount(line[key]),
    })),
    {
      title: t('currencies.rate', { ns: 'officialTravel' }),
      key: 'rate',
      align: 'right',
      render: (_, line) => {
        if (line.currency === TRAVEL_DOMESTIC_CURRENCY) return '1';
        return line.rate ? formatRate(line.rate) : '-';
      },
    },
    {
      title: t('currencies.totalAmountForPaymentRsd', { ns: 'officialTravel' }),
      key: 'totalAmountForPaymentRsd',
      align: 'right',
      render: (_, line) => (
        <span className="font-semibold">
          {formatAmount(line.totalAmountForPaymentRsd)}
        </span>
      ),
    },
  ];

  const hasMissingRate = currencies.some(
    (line) => line.currency !== TRAVEL_DOMESTIC_CURRENCY && !line.rate
  );

  return (
    <div className="grid gap-4">
      {rates.length > 0 && (
        <div className="grid gap-2">
          <h4 className="font-semibold">
            {t('currencies.ratesTitle', { ns: 'officialTravel' })}
          </h4>
          <p className="text-sm text-zinc-600">
            {t('currencies.ratesHint', { ns: 'officialTravel' })}
          </p>
          <Table
            dataSource={rates}
            columns={rateColumns}
            pagination={false}
            bordered
            size="small"
            rowKey="currency"
            scroll={{ x: true }}
          />
          {!disabled && (
            <div className="flex justify-end">
              <Button
                type="primary"
                shape="round"
                className="btn-primary"
                disabled={isPending}
                loading={isPending}
                onClick={() => onSubmit(rates)}
              >
                {t('actions.saveRates', { ns: 'officialTravel' })}
              </Button>
            </div>
          )}
        </div>
      )}

      {currencies.length > 0 && (
        <div className="grid gap-2">
          <h4 className="font-semibold">
            {t('currencies.title', { ns: 'officialTravel' })}
          </h4>
          {hasMissingRate && (
            <p className="text-sm text-red-600">
              {t('currencies.missingRate', { ns: 'officialTravel' })}
            </p>
          )}
          <Table
            dataSource={currencies}
            columns={settlementColumns}
            pagination={false}
            bordered
            size="small"
            rowKey="currency"
            scroll={{ x: true }}
            summary={() => (
              <Table.Summary.Row className="font-semibold">
                <Table.Summary.Cell index={0} colSpan={6}>
                  {t('currencies.totalRsd', { ns: 'officialTravel' })}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  {formatAmount(
                    travelOrderCalculation?.totalAmountForPaymentRsd,
                    TRAVEL_DOMESTIC_CURRENCY
                  )}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </div>
      )}
    </div>
  );
}
